import { createSlice, current } from '@reduxjs/toolkit';
import { REGIONS_KEYS } from '@sonifi/wb-screenbuilder/components/HappeningsBuilder/constants/wbHBBuilderConstants';
import { parseHexToColor } from '@sonifi/wb-screenbuilder/components/HappeningsBuilder/services/color.service';

const initialState = {
  activeField: null,
  canvasLoaded: false,
  layouts: [],
  layoutSelected: null,
  overlays: null,
  regionsWorked: {},
  slideSelected: null,
  showImageSlider: false,
};

const buildOverlay = (layoutFound) => {
  const overlays = {};

  Object.values(REGIONS_KEYS).forEach((key) => {
    if (layoutFound.regions[key] && key !== REGIONS_KEYS.background) {
      overlays[key] = {
        show: true,
        top: layoutFound.regions[key].y,
        left: layoutFound.regions[key].x,
        width: layoutFound.regions[key].width,
        height: layoutFound.regions[key].height,
      };
    }
    if (!layoutFound.regions[key] && key !== REGIONS_KEYS.background) {
      overlays[key] = {
        show: false,
      };
    }
  });
  return overlays;
};

export const happeningsSlideEditorSlice = createSlice({
  name: 'happeningsSlideEditor',
  initialState,
  reducers: {
    setSlideSelected(state, action) {
      state.slideSelected = action.payload;
    },
    setHBLayoutById(state, action) {
      const { layouts, regionsWorked } = current(state);

      const layoutId = action.payload;
      const layoutFound = layouts.find((ly) => ly.id === layoutId);

      const currentRegionsWorked = { ...regionsWorked };
      const regions = {};
      Object.keys(layoutFound.regions).forEach((key) => {
        if (Object.getOwnPropertyDescriptor(currentRegionsWorked, key)) {
          regions[key] = {
            ...currentRegionsWorked[key],
            sequence: layoutFound.regions[key].sequence,
            x: layoutFound.regions[key].x,
            y: layoutFound.regions[key].y,
            width: layoutFound.regions[key].width,
            height: layoutFound.regions[key].height,
          };
        } else {
          const newRegion = {
            ...layoutFound.regions[key],
            bg: parseHexToColor(layoutFound.regions[key].background_color),
            id: key,
            isSelected: false,
          };
          regions[key] = newRegion;
          currentRegionsWorked[key] = newRegion;
        }
      });

      const overlays = buildOverlay(layoutFound);

      state.overlays = overlays;
      state.activeField = regions.Background;
      state.regionsWorked = currentRegionsWorked;
      state.layoutSelected = {
        ...layoutFound,
        regions,
      };
    },
    setHBLayout(state, action) {
      const layout = action.payload;
      const overlays = buildOverlay(layout);
      state.overlays = overlays;
      state.layoutSelected = action.payload;
      state.regionsWorked = action.payload.regions;
      state.activeField = action.payload.regions.Background;
    },
    setHBLayouts(state, action) {
      state.layouts = action.payload;
    },
    setHBFieldSelected(state, action) {
      const { layoutSelected } = current(state);
      const regionId = action.payload;
      const regions = {};
      Object.values(layoutSelected.regions).forEach((region) => {
        regions[region.id] = {
          ...layoutSelected.regions[region.id],
          isSelected: region.id === regionId,
        };
      });
      state.layoutSelected = {
        ...layoutSelected,
        regions,
      };
      state.activeField = { ...regions[regionId] };
    },
    updateRegionSelected(state, action) {
      const { region } = action.payload;
      const { layoutSelected, regionsWorked } = current(state);

      const regions = {};
      Object.values(layoutSelected.regions).forEach((reg) => {
        regions[reg.id] = reg.id === region.id ? region : reg;
      });
      state.layoutSelected = {
        ...layoutSelected,
        regions,
      };
      state.regionsWorked = {
        ...regionsWorked,
        [region.id]: region,
      };
      state.activeField = region;
    },
    setShowImageSlider(state, action) {
      state.showImageSlider = action.payload;
    },
    setShowColorPicker(state, action) {
      state.showColorPicker = action.payload;
    },
    setShowOpacitySlider(state, action) {
      state.showOpacitySlider = action.payload;
    },
    setCanvasLoaded(state, action) {
      state.canvasLoaded = action.payload;
    },
    cleanHappeningsSlideEditorSlice() {
      return initialState;
    },
  },
});

export const {
  cleanHappeningsSlideEditorSlice,
  setCanvasLoaded,
  setHBLayout,
  setHBLayoutById,
  setHBLayouts,
  setHBFieldSelected,
  setSlideSelected,
  setShowColorPicker,
  setShowImageSlider,
  setShowOpacitySlider,
  updateRegionSelected,
} = happeningsSlideEditorSlice.actions;

export default happeningsSlideEditorSlice.reducer;
